import React from "react"
import { useState } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Card, CardContent } from "../../../components/ui/card"
import { Building2, Loader2 } from "lucide-react"
import { toast } from "react-toastify";
// @ts-ignore
import { useDropdownData } from "../../Services/Common.tsx";
// @ts-ignore
import { useTenantManagement } from "../../Services/TenantManagement.tsx";
import { inputFieldValidator } from "../../../Utilities/validation";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";
import { useUIContext } from "../../../Contexts/UIProvider";

const INITIAL_PROFILE_DTO = {
  code: null as string | null,
  name: null as string | null,
  legalName: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  industryId: null as string | null,
  companyTypeId: null as string | null,
  businessRegNumber: "",
  countryId: null as string | null,
  email: "",
  telephone: "",
  website: "",
  currency: "",
  timeZone: "",
  fiscalYearStart: new Date(),
  fiscalYearEnd: new Date(),
  logo: "",
  defaultLanguage: "",
  billingContactName: "",
  billingContactEmail: "",
  billingContactPhone: "",
  description: undefined as string | undefined,
};

const INITIAL_ERRORS = {
  code: false,
  name: false,
  companyTypeId: false,
  industryId: false,
  countryId: false,
  description: false,
};

const INPUT_CLASS = "mt-1 bg-background text-foreground border-input";

const CompanyRegistration = (props) => {
  const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
  const [profileDto, setProfileDto] = useState({ ...INITIAL_PROFILE_DTO });

  const { companyTypes, industries, countries } = useDropdownData();
  const { createTenant } = useTenantManagement();
  const { notifyAccountDashboardRefresh } = useUIContext();
  const [loading, setLoading] = useState(false);

  const onChange = (event, type?) => {
    const detailType = type ? type : event?.target?.name;
    const detailValue = type ? event : event?.target?.value;

    setProfileDto((prev) => ({
      ...prev,
      [detailType]: detailValue,
    }));

    if (detailType in INITIAL_ERRORS) {
      setErrors((prev) => ({ ...prev, [detailType]: false }));
    }
  };

  const safeCall = (fn: any, value: any) => {
    if (typeof fn === "function") fn(value);
  };

  const resetForm = () => {
    setProfileDto({ ...INITIAL_PROFILE_DTO, fiscalYearStart: new Date(), fiscalYearEnd: new Date() });
    setErrors({ ...INITIAL_ERRORS });
    setLoading(false);
  };

  const handleRegistrationError = (res: unknown) => {
    const message = getApiErrorMessage(res);
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes("already registered") || lowerMessage.includes("tenant '")) {
      setErrors((prev) => ({ ...prev, name: true, code: false }));
      toast.error(message);
      return;
    }

    if (lowerMessage.includes("code") || lowerMessage.includes("company code")) {
      setErrors((prev) => ({ ...prev, code: true }));
      toast.error("Company code already exists. Please choose a different code.");
      return;
    }

    toast.error(message || "Organization creation failed");
  };

  const handleSubmit = () => {
    setLoading(true);
    const newErrors = {
      code: inputFieldValidator(profileDto?.code, "string"),
      name: inputFieldValidator(profileDto?.name, "string"),
      companyTypeId: inputFieldValidator(profileDto?.companyTypeId, "string"),
      industryId: inputFieldValidator(profileDto?.industryId, "string"),
      countryId: inputFieldValidator(profileDto?.countryId, "string"),
      description: inputFieldValidator(profileDto?.description, "string"),
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((v) => v);
    if (hasError) return setLoading(false);

    createTenant(profileDto)
      .then((res) => {
        if (isApiError(res)) {
          handleRegistrationError(res);
          setLoading(false);
          return;
        }

        safeCall(props?.setIsContentLoad, true);
        safeCall(props?.setIsShow, false);
        safeCall(props?.setIsRegisterTenant, false);
        safeCall(props?.setIsDialogOpen, true);
        safeCall(props?.onSuccessCallBack, null);
        notifyAccountDashboardRefresh();

        toast.success("Organization Created");
        resetForm();
        setLoading(false);
      })
      .catch((error) => {
        if (isApiError(error)) {
          handleRegistrationError(error);
        } else {
          toast.error("Organization creation failed");
        }
        setLoading(false);
      });
  };

  const handleCancel = () => {
    resetForm();
    props?.onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    props?.onOpenChange(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <Sheet open={props?.open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
        <div className="bg-background h-full text-foreground">
          <SheetHeader className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <SheetTitle className="text-xl font-bold">Company Registration</SheetTitle>
                  <p className="text-sm mt-1">Complete your blockchain integration setup</p>
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="p-6">
            <Card className="mb-6">
              <CardContent className="p-4">
                <p className="mb-2">
                  <strong>Thanks for registering with {"Elystrain"}!</strong>
                </p>
                <p className="text-sm">
                  {"You're just a step away from setting up your store for blockchain integration."}
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold mb-6">Tell us about your company,</h2>

            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="companyCode" className="text-sm font-medium">
                    Company Code
                  </Label>
                  <Input
                    id="companyCode"
                    type="text"
                    name="code"
                    placeholder="Company Code"
                    maxLength={4}
                    autoComplete="off"
                    value={profileDto?.code || ""}
                    onChange={(event) => {
                      const value = event.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 4);
                      onChange(value, "code");
                      setErrors((prev) => ({ ...prev, code: false }));
                    }}
                    className={`${INPUT_CLASS} ${errors.code ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                </div>

                <div>
                  <Label htmlFor="companyName" className="text-sm font-medium">
                    Company Name
                  </Label>
                  <Input
                    id="companyName"
                    type="text"
                    name="name"
                    placeholder="Company Name"
                    autoComplete="organization"
                    value={profileDto?.name || ""}
                    onChange={(event) => {
                      onChange(event);
                      setErrors((prev) => ({ ...prev, name: false }));
                    }}
                    className={`${INPUT_CLASS} ${errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                </div>

                <div>
                  <Label htmlFor="companyType" className="text-sm font-medium">
                    Company Type
                  </Label>
                  <Select
                    value={profileDto?.companyTypeId || undefined}
                    onValueChange={(event) => onChange(event, "companyTypeId")}
                  >
                    <SelectTrigger className={errors.companyTypeId ? "border-red-500 focus-visible:ring-red-500" : ""}>
                      <SelectValue placeholder="Select the Company Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypes.map((item, index) => (
                        <SelectItem key={index} value={item?.id}>
                          {item?.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="industry" className="text-sm font-medium">
                    Industry
                  </Label>
                  <Select
                    value={profileDto?.industryId || undefined}
                    onValueChange={(event) => onChange(event, "industryId")}
                  >
                    <SelectTrigger className={errors.industryId ? "border-red-500 focus-visible:ring-red-500" : ""}>
                      <SelectValue placeholder="Select the Industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((item, index) => (
                        <SelectItem key={index} value={item?.id}>
                          {item?.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="country" className="text-sm font-medium">
                    Country
                  </Label>
                  <Select
                    value={profileDto?.countryId ? profileDto?.countryId : undefined}
                    onValueChange={(event) => onChange(event, "countryId")}
                  >
                    <SelectTrigger className={errors.countryId ? "border-red-500 focus-visible:ring-red-500" : ""}>
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries?.map((country) => (
                        <SelectItem key={country?.id} value={country?.id}>
                          {country?.countryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description" className="text-sm font-medium">
                    Additional Information
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Type your message here"
                    className={`${INPUT_CLASS} min-h-[300px] resize-none ${errors.description ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    name="description"
                    value={profileDto?.description || ""}
                    onChange={(event) => {
                      onChange(event);
                      setErrors((prev) => ({ ...prev, description: false }));
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t">
                <Button variant="outline" size="lg" className="px-8 py-3 text-base font-medium mr-4" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-base font-medium"
                  size="lg"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Register your Company"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default CompanyRegistration;
